import React, { useState, useEffect } from 'react';
import { getBookedSlots, getAvailableTimeSlots, isTimeSlotInPast, formatTimeForDisplay } from '../services/calendarService';
import './AppointmentCalendar.css';

const AppointmentCalendar = ({ selectedDate, onTimeSlotSelect, onDateChange }) => {
  const [bookedSlots, setBookedSlots] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [viewDate, setViewDate] = useState(selectedDate || new Date());

  useEffect(() => {
    if (selectedDate) {
      setViewDate(selectedDate instanceof Date ? selectedDate : new Date(selectedDate));
    }
  }, [selectedDate]);

  useEffect(() => {
    loadCalendarData();
  }, [viewDate]);

  const loadCalendarData = async () => {
    try {
      setLoading(true);
      const [booked, available] = await Promise.all([
        getBookedSlots(viewDate),
        getAvailableTimeSlots(viewDate)
      ]);
      setBookedSlots(booked);
      setAvailableSlots(available);
    } catch (error) {
      console.error('Error loading calendar data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (e) => {
    const newDate = new Date(e.target.value);
    setViewDate(newDate);
    if (onDateChange) {
      onDateChange(newDate);
    }
  };

  const handleSlotClick = (time, isBooked, appointment) => {
    if (isBooked) {
      setSelectedSlot({ time, appointment });
    } else if (!isTimeSlotInPast(viewDate, time)) {
      setSelectedSlot({ time, appointment: null });
      if (onTimeSlotSelect) {
        onTimeSlotSelect(time);
      }
    }
  };

  const generateTimeSlots = () => {
    const slots = [];
    const dayOfWeek = viewDate.getDay();
    const startHour = 8;
    const endHour = dayOfWeek === 6 ? 18 : 20; // Saturday ends at 6 PM
    
    for (let hour = startHour; hour < endHour; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        slots.push(timeString);
      }
    }
    
    return slots;
  };

  const getSlotStatus = (time) => {
    if (isTimeSlotInPast(viewDate, time)) {
      return 'past';
    }
    
    const bookedSlot = bookedSlots.find(slot => slot.time === time);
    if (bookedSlot) {
      return 'booked';
    }
    
    if (availableSlots.includes(time)) {
      return 'available';
    }
    
    return 'unavailable';
  };

  const getSlotAppointment = (time) => {
    const bookedSlot = bookedSlots.find(slot => slot.time === time);
    return bookedSlot ? bookedSlot.appointment : null;
  };

  const formatDateForInput = (date) => {
    const d = date instanceof Date ? date : new Date(date);
    return d.toISOString().split('T')[0];
  };

  const formatDateForDisplay = (date) => {
    const d = date instanceof Date ? date : new Date(date);
    return d.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const navigateDate = (days) => {
    const newDate = new Date(viewDate);
    newDate.setDate(newDate.getDate() + days);
    setViewDate(newDate);
    if (onDateChange) {
      onDateChange(newDate);
    }
  };

  const timeSlots = generateTimeSlots();

  return (
    <div className="appointment-calendar">
      <div className="calendar-header">
        <div className="calendar-date-navigation">
          <button 
            type="button"
            onClick={() => navigateDate(-1)} 
            className="calendar-nav-btn"
            aria-label="Previous day"
          >
            ←
          </button>
          <input
            type="date"
            value={formatDateForInput(viewDate)}
            onChange={handleDateChange}
            className="calendar-date-picker"
            min={new Date().toISOString().split('T')[0]}
          />
          <button 
            type="button"
            onClick={() => navigateDate(1)} 
            className="calendar-nav-btn"
            aria-label="Next day"
          >
            →
          </button>
        </div>
        <h3 className="calendar-date-display">{formatDateForDisplay(viewDate)}</h3>
      </div>

      {loading ? (
        <div className="calendar-loading">
          <p>Loading availability...</p>
        </div>
      ) : (
        <>
          <div className="calendar-legend">
            <div className="legend-item">
              <span className="legend-color legend-available"></span>
              <span>Available</span>
            </div>
            <div className="legend-item">
              <span className="legend-color legend-booked"></span>
              <span>Booked</span>
            </div>
            <div className="legend-item">
              <span className="legend-color legend-past"></span>
              <span>Past</span>
            </div>
          </div>

          <div className="calendar-time-slots">
            {timeSlots.map((time) => {
              const status = getSlotStatus(time);
              const appointment = getSlotAppointment(time);
              const isSelected = selectedSlot && selectedSlot.time === time;

              return (
                <div
                  key={time}
                  className={`time-slot time-slot-${status} ${isSelected ? 'time-slot-selected' : ''}`}
                  onClick={() => handleSlotClick(time, status === 'booked', appointment)}
                  title={
                    status === 'booked' && appointment
                      ? `Booked: ${appointment.name} - ${appointment.service}`
                      : status === 'available'
                      ? 'Click to book this time slot'
                      : status === 'past'
                      ? 'This time has passed'
                      : 'Unavailable'
                  }
                >
                  <div className="time-slot-time">{formatTimeForDisplay(time)}</div>
                  {status === 'booked' && appointment && (
                    <div className="time-slot-details">
                      <div className="slot-patient-name">{appointment.name}</div>
                      <div className="slot-service">{appointment.service}</div>
                    </div>
                  )}
                  {status === 'available' && (
                    <div className="time-slot-available-indicator">Available</div>
                  )}
                </div>
              );
            })}
          </div>

          {selectedSlot && selectedSlot.appointment && (
            <div className="calendar-appointment-details">
              <h4>Appointment Details</h4>
              <div className="appointment-detail-item">
                <strong>Patient:</strong> {selectedSlot.appointment.name}
              </div>
              <div className="appointment-detail-item">
                <strong>Service:</strong> {selectedSlot.appointment.service}
              </div>
              <div className="appointment-detail-item">
                <strong>Time:</strong> {formatTimeForDisplay(selectedSlot.time)}
              </div>
              {selectedSlot.appointment.phone && (
                <div className="appointment-detail-item">
                  <strong>Phone:</strong> {selectedSlot.appointment.phone}
                </div>
              )}
              {selectedSlot.appointment.email && (
                <div className="appointment-detail-item">
                  <strong>Email:</strong> {selectedSlot.appointment.email}
                </div>
              )}
              {selectedSlot.appointment.message && (
                <div className="appointment-detail-item">
                  <strong>Notes:</strong> {selectedSlot.appointment.message}
                </div>
              )}
              <div className="appointment-detail-item">
                <strong>Status:</strong> 
                <span className={`status-badge status-${selectedSlot.appointment.status}`}>
                  {selectedSlot.appointment.status}
                </span>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AppointmentCalendar;
